<template>
  <div class="converter-container">
    <h2 class="mb-4">SGDE -> AED Converter</h2>
    <p class="mb-6">
      Upload a <code>.sgde</code> export from DTS Monaco and this tool will
      build a dedicated <code>.aed</code> file for each ECU module it finds. All
      modules are bundled into a ZIP archive you can download.
    </p>

    <v-file-input
      label="Select SGDE file"
      accept=".sgde,.xml,text/xml"
      show-size
      density="comfortable"
      prepend-icon="mdi-file-upload"
      @update:model-value="handleFile"
    />

    <v-alert
      v-if="error"
      type="error"
      class="mt-4"
      border="start"
      variant="tonal"
    >
      {{ error }}
    </v-alert>

    <v-progress-linear
      v-if="isProcessing"
      indeterminate
      color="primary"
      class="mt-4"
    />

    <v-alert
      v-if="modules.length > 1"
      type="info"
      class="mt-4"
      border="start"
      variant="tonal"
    >
      Detected {{ modules.length }} ECU modules. The download will contain a ZIP
      with individual AED files for each module.
    </v-alert>

    <v-card v-if="metadata" class="mt-6" variant="outlined">
      <v-card-title>Detected Metadata</v-card-title>
      <v-card-text>
        <v-row dense>
          <v-col cols="12" md="4">
            <div class="meta-label">Product Version</div>
            <div class="meta-value">{{ metadata.productVersion }}</div>
          </v-col>
          <v-col cols="12" md="4">
            <div class="meta-label">Creator</div>
            <div class="meta-value">{{ metadata.creator }}</div>
          </v-col>
          <v-col cols="12" md="4">
            <div class="meta-label">Records Found</div>
            <div class="meta-value">{{ metadata.noOfDomains }}</div>
          </v-col>
          <v-col cols="12" md="4">
            <div class="meta-label">Date</div>
            <div class="meta-value">{{ metadata.date }}</div>
          </v-col>
          <v-col cols="12" md="4">
            <div class="meta-label">Time</div>
            <div class="meta-value">{{ metadata.time }}</div>
          </v-col>
          <v-col cols="12" md="4">
            <div class="meta-label">Author</div>
            <div class="meta-value">{{ metadata.author }}</div>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <v-card v-if="modules.length" class="mt-6" variant="outlined">
      <v-card-title>Modules ({{ modules.length }})</v-card-title>
      <v-card-text>
        <v-list density="comfortable">
          <v-list-item
            v-for="(module, index) in modules"
            :key="module.fileName"
            :active="selectedModuleIndex === index"
            @click="selectModule(index)"
          >
            <template #prepend>
              <v-icon>mdi-chip</v-icon>
            </template>
            <v-list-item-title>{{ module.name }}</v-list-item-title>
            <v-list-item-subtitle>
              {{ module.records }} coding entries - {{ module.fileName }}.aed
            </v-list-item-subtitle>
            <template #append>
              <v-btn
                icon="mdi-download"
                variant="text"
                :aria-label="`Download ${module.fileName}.aed`"
                @click.stop="downloadModule(module)"
              ></v-btn>
            </template>
          </v-list-item>
        </v-list>
      </v-card-text>
    </v-card>

    <v-textarea
      v-model="aedContent"
      class="mt-6"
      rows="12"
      auto-grow
      :label="
        selectedModule
          ? `AED preview (${selectedModule.fileName}.aed)`
          : 'AED preview'
      "
      readonly
      outlined
    />

    <div class="action-row mt-4">
      <v-btn
        color="primary"
        :disabled="!modules.length"
        prepend-icon="mdi-zip-box"
        @click="downloadZip"
      >
        Download ZIP
      </v-btn>
      <v-btn class="ml-4" variant="tonal" color="secondary" @click="resetForm">
        Reset
      </v-btn>
    </div>
  </div>
</template>

<script>
import JSZip from "jszip";

export default {
  name: "SgdeToAed",
  data() {
    return {
      error: "",
      isProcessing: false,
      sgdeContent: "",
      aedContent: "",
      metadata: null,
      originalFileName: "",
      modules: [],
      selectedModuleIndex: 0,
    };
  },
  computed: {
    selectedModule() {
      return this.modules[this.selectedModuleIndex] || null;
    },
  },
  methods: {
    handleFile(file) {
      this.resetOutput();
      const selectedFile = Array.isArray(file) ? file[0] : file;

      if (!selectedFile) {
        return;
      }

      this.originalFileName = selectedFile.name || "converted.sgde";
      const reader = new FileReader();
      reader.onload = (event) => {
        this.sgdeContent = event.target.result;
        this.convertSgdeToAed();
      };
      reader.onerror = () => {
        this.error = "Unable to read the provided file.";
      };
      reader.readAsText(selectedFile);
    },
    resetOutput() {
      this.error = "";
      this.aedContent = "";
      this.metadata = null;
      this.modules = [];
      this.selectedModuleIndex = 0;
    },
    resetForm() {
      this.resetOutput();
      this.sgdeContent = "";
      this.originalFileName = "";
    },
    convertSgdeToAed() {
      if (!this.sgdeContent) {
        this.error = "No SGDE content provided.";
        return;
      }

      this.isProcessing = true;

      try {
        const parser = new DOMParser();
        const xmlDoc = parser.parseFromString(
          this.sgdeContent,
          "application/xml"
        );

        if (xmlDoc.querySelector("parsererror")) {
          throw new Error("Unable to parse SGDE XML. Please verify the file.");
        }

        const modules = this.extractModules(xmlDoc);

        this.modules = modules;
        this.selectedModuleIndex = 0;
        this.aedContent = modules[0].content;

        const totalRecords = modules.reduce(
          (sum, module) => sum + module.records,
          0
        );
        this.metadata = this.extractMetadata(xmlDoc, {
          configTitle:
            modules.length === 1 ? modules[0].name : "Multiple Modules",
          noOfDomains: totalRecords,
        });
      } catch (err) {
        this.error =
          err?.message || "Unexpected error while converting SGDE file.";
      } finally {
        this.isProcessing = false;
      }
    },
    extractModules(xmlDoc) {
      const ecuRecords = Array.from(
        xmlDoc.querySelectorAll("Content > EcuRecords > EcuRecord")
      );

      if (!ecuRecords.length) {
        throw new Error("No ECU modules found in the SGDE file.");
      }

      const modules = [];

      ecuRecords.forEach((ecuRecord, index) => {
        const moduleName =
          ecuRecord
            .querySelector("EcuIdentification > EcuName")
            ?.textContent?.trim() ||
          ecuRecord
            .querySelector("DiagnosticVariantName")
            ?.textContent?.trim() ||
          `Module_${index + 1}`;

        const codingRecords = Array.from(
          ecuRecord.querySelectorAll("CodingDataRecord")
        );

        if (!codingRecords.length) {
          return;
        }

        const metadata = this.extractMetadata(xmlDoc, {
          configTitle: moduleName,
          noOfDomains: codingRecords.length,
        });

        const recordLines = codingRecords.map((record) => {
          const serviceName = record
            .querySelector("ServiceName")
            ?.textContent?.trim();
          const codingString =
            record.querySelector("CodingString")?.textContent?.trim() ?? "";

          if (!serviceName) {
            throw new Error(
              "Encountered a coding record without a ServiceName."
            );
          }

          const key = this.toAedKey(serviceName);
          const formattedValue = this.formatCodingString(codingString);
          return `${key}=${formattedValue}`;
        });

        modules.push({
          name: moduleName,
          content: `${this.buildHeader(metadata)}\r\n${recordLines.join(
            "\r\n"
          )}\r\n`,
          records: codingRecords.length,
          fileName: this.toSafeFileName(moduleName, index),
        });
      });

      if (!modules.length) {
        throw new Error(
          "No coding records found for the detected ECU modules."
        );
      }

      return modules;
    },
    extractMetadata(xmlDoc, overrides = {}) {
      const modificationTime =
        xmlDoc
          .querySelector("Information > General > ModificationTime")
          ?.textContent?.trim() ?? "";
      const author = "NPCODE";
      const toolVersion =
        xmlDoc
          .querySelector("Information > General > Tool > Version")
          ?.textContent?.trim() ?? "Unknown";
      const kernelName =
        xmlDoc
          .querySelector("Information > Diagnostic > Kernel > Name")
          ?.textContent?.trim() ?? "Softing";

      const parsedDate = modificationTime
        ? new Date(modificationTime)
        : new Date();
      const pad = (value) => value.toString().padStart(2, "0");

      const day = pad(parsedDate.getDate());
      const month = pad(parsedDate.getMonth() + 1);
      const year = parsedDate.getFullYear();
      const hours = pad(parsedDate.getHours());
      const minutes = pad(parsedDate.getMinutes());
      const seconds = pad(parsedDate.getSeconds());

      return {
        title: "DTS Monaco additional ECU data",
        productVersion: toolVersion,
        schema: "7.01",
        creator: kernelName,
        date: `${day}/${month}/${year}`,
        time: `${hours}:${minutes}:${seconds}`,
        author,
        comment: "",
        noOfConfigurations: 1,
        configTitle: overrides.configTitle ?? "aed",
        configComment: "",
        noOfDomains: overrides.noOfDomains ?? 0,
      };
    },
    buildHeader(meta) {
      return [
        "[Description]",
        `Title=${meta.title}`,
        `ProductVersion=${meta.productVersion}`,
        `Schema=${meta.schema}`,
        `Creator=${meta.creator}`,
        `Date=${meta.date}`,
        `Time=${meta.time}`,
        `Author=${meta.author}`,
        `Comment=${meta.comment}`,
        `NoOfConfigurations=${meta.noOfConfigurations}`,
        "[Configuration_001]",
        `NoOfDomains=${meta.noOfDomains}`,
        `Title=${meta.configTitle}`,
        `Comment=${meta.configComment}`,
      ].join("\r\n");
    },
    toAedKey(serviceName) {
      const baseName = serviceName
        .replace(/_Write_Dump$/i, "")
        .replace(/_Write$/i, "")
        .replace(/_Dump$/i, "");

      return `VCD_${baseName.replace(/[^A-Za-z0-9_]/g, "_")}`;
    },
    formatCodingString(rawValue) {
      if (!rawValue) {
        return "";
      }

      const sanitized = rawValue.replace(/[^a-fA-F0-9]/g, "").toUpperCase();
      if (!sanitized) {
        return "";
      }

      const evenLength =
        sanitized.length % 2 === 0
          ? sanitized
          : `${sanitized.padEnd(sanitized.length + 1, "0")}`;

      const pairs = evenLength.match(/.{1,2}/g) || [];
      return pairs.join(",");
    },
    selectModule(index) {
      this.selectedModuleIndex = index;
      this.aedContent = this.modules[index]?.content ?? "";
    },
    toSafeFileName(name, index) {
      const sanitized = name
        .replace(/[^A-Za-z0-9-_]+/g, "_")
        .replace(/_+/g, "_")
        .replace(/^_+|_+$/g, "");
      return sanitized || `module_${index + 1}`;
    },
    async downloadZip() {
      if (!this.modules.length) {
        return;
      }

      try {
        const zip = new JSZip();
        this.modules.forEach((module) => {
          zip.file(`${module.fileName}.aed`, module.content);
        });

        const baseName =
          this.originalFileName?.replace(/\.(sgde|xml)$/i, "") || "converted";
        const blob = await zip.generateAsync({ type: "blob" });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `${baseName}.zip`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } catch (err) {
        this.error = "Failed to create the ZIP archive.";
      }
    },
    downloadModule(module) {
      if (!module) {
        return;
      }

      const blob = new Blob([module.content], { type: "text/plain" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${module.fileName}.aed`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
  },
};
</script>

<style scoped>
.converter-container {
  padding: 24px;
}

.meta-label {
  font-size: 0.75rem;
  color: #666;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.meta-value {
  font-weight: 600;
  font-size: 1rem;
}

.action-row {
  display: flex;
  align-items: center;
}
</style>
